/**
 * Subunit API — CRUD for subunits (units with parent_unit_id)
 */

import { supabase } from '@/lib/supabase'
import type { SubunitRow } from '@/types/subunit'

/**
 * Fetch subunits for a parent unit (soft-delete filtered)
 */
export async function fetchSubunits(parentUnitId: string): Promise<SubunitRow[]> {
  const { data, error } = await supabase
    .from('units')
    .select('*')
    .eq('parent_unit_id', parentUnitId)
    .is('deleted_at', null)
    .order('created_at')

  if (error) throw error
  return (data as unknown as SubunitRow[]) ?? []
}

/**
 * Fetch all subunits for a location (for 3D rendering)
 */
export async function fetchSubunitsByLocation(locationId: string): Promise<SubunitRow[]> {
  const { data, error } = await supabase
    .from('units')
    .select('*, areas!inner(location_id)')
    .not('parent_unit_id', 'is', null)
    .is('deleted_at', null)
    .eq('areas.location_id', locationId)
    .order('parent_unit_id')
    .order('created_at')

  if (error) throw error
  return (data as unknown as SubunitRow[]) ?? []
}

/**
 * Create a subunit
 */
export async function createSubunit(
  insert: Record<string, unknown>
): Promise<SubunitRow> {
  const { data, error } = await supabase
    .from('units')
    .insert(insert)
    .select()
    .single()

  if (error) throw error
  return data as unknown as SubunitRow
}

/**
 * Update a subunit
 */
export async function updateSubunit(
  id: string,
  update: Record<string, unknown>
): Promise<SubunitRow> {
  const { data, error } = await supabase
    .from('units')
    .update(update)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data as unknown as SubunitRow
}

/**
 * Soft-delete a subunit
 */
export async function deleteSubunit(id: string): Promise<void> {
  const { error } = await supabase
    .from('units')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}
